import React from "react";
import { Link } from "react-router-dom";

const focusAreas = [
  {
    no: "01",
    title: "Development & Infrastructure",
    body: "Residential, hospitality, destination development and enabling infrastructure that establishes long-term physical and economic foundations.",
    pillars: ["Residential", "Hospitality", "Destination", "Infrastructure"],
  },
  {
    no: "02",
    title: "Food Security & Agro-Industry",
    body: "Import substitution, food sovereignty, processing and value-added export platforms that strengthen national resilience and rural economies.",
    pillars: ["Import Substitution", "Food Sovereignty", "Processing", "Exports", "Value Chains"],
  },
  {
    no: "03",
    title: "Healthcare & Community Impact",
    body: "Healthcare infrastructure, wellness, hospice care and community development investments aligned to sustained social impact.",
    pillars: ["Healthcare", "Wellness", "Hospice Care", "Community", "Social Impact"],
  },
];

const pillars = [
  { title: "Governance", body: "Strong oversight, clear accountability and disciplined decision-making at every level." },
  { title: "Strategic Alignment", body: "Projects deliberately aligned with national priorities and long-term economic agendas." },
  { title: "Investor Protection", body: "Robust structures, transparent reporting and rigorous risk frameworks." },
  { title: "Sustainable Impact", body: "Investments that generate measurable economic and social value over decades." },
  { title: "Scalable Platforms", body: "Architected for expansion across geographies, sectors and capital structures." },
  { title: "Execution Focus", body: "Experienced leadership translating strategy into delivery on the ground." },
];

const platforms = [
  {
    label: "Platform 01",
    title: "Bambereaux Integrated Development Platform",
    location: "Saint Vincent & the Grenadines",
    summary:
      "Luxury residential, hospitality and healthcare destination development on the south coast — a flagship integrated platform combining real estate, tourism and community impact.",
    tags: ["Residential", "Hospitality", "Healthcare"],
    metric: "US$158M+",
    metricLabel: "Projected platform value",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/0owokzqi_IMG_0620.jpeg",
    to: "/bambereaux",
    testId: "platform-card-bambereaux",
  },
  {
    label: "Platform 02",
    title: "National Agro-Industrial Import Substitution Platform",
    location: "Saint Vincent & the Grenadines",
    summary:
      "Integrated tomato processing platform anchoring food sovereignty, farmer participation and CARICOM export capacity through a vertically integrated value chain.",
    tags: ["Food Sovereignty", "Processing", "CARICOM Export"],
    metric: "120 Acres",
    metricLabel: "Initial cultivation footprint",
    image: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/6wx54trf_IMG_0623.jpeg",
    to: "/agro-industrial-platform",
    testId: "platform-card-agro",
  },
];

const whoWeAreItems = [
  "Identify strategic opportunities",
  "Structure investment platforms",
  "Establish governance frameworks",
  "Coordinate stakeholder engagement",
  "Attract institutional capital",
  "Deliver long-term value creation",
];

export default function Home() {
  return (
    <>
      {/* HERO — photography-dominant */}
      <section
        data-testid="home-hero"
        className="relative bg-[#0A1F44] text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/crupgogi_IMG_0622.jpeg)",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[#0A1F44]/40" aria-hidden="true" />
        <div
          className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/90 via-[#0A1F44]/35 to-transparent"
          aria-hidden="true"
        />

        <div className="relative evg-container min-h-[88vh] flex flex-col justify-end pt-32 pb-24 md:pb-32">
          <div className="max-w-5xl" data-reveal>
            <div className="evg-overline-gold mb-10">Evergreen Global Holdings Ltd</div>
            <h1 className="evg-display-xl text-white">
              Building Strategic Platforms That Create Long-Term Economic Value
            </h1>
            <p className="evg-lead evg-lead-light mt-12 max-w-2xl">
              Evergreen Global Holdings Ltd is a UK-based development sponsor, investment platform
              and governance-led holding company focused on creating long-term value through
              strategically important projects across the Caribbean.
            </p>

            <div className="mt-14 flex flex-col sm:flex-row gap-x-10 gap-y-4">
              <Link
                to="/projects"
                data-testid="hero-cta-platforms"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-white border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors self-start"
              >
                View Strategic Platforms →
              </Link>
              <Link
                to="/investor-relations"
                data-testid="hero-cta-investor"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-white/65 border-b border-white/25 pb-2 hover:text-white hover:border-white transition-colors self-start"
              >
                Investor Relations
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom meta strip — editorial */}
        <div className="relative border-t border-white/12">
          <div className="evg-container py-6">
            <div className="flex flex-wrap items-baseline gap-x-12 gap-y-3 text-white/65">
              <span className="evg-meta evg-meta-light">02 Strategic Platforms</span>
              <span className="evg-meta evg-meta-light">United Kingdom</span>
              <span className="evg-meta evg-meta-light">Caribbean Focus</span>
              <span className="evg-meta evg-meta-light">Long-Term Horizon</span>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE — editorial two-column */}
      <section data-testid="home-who-we-are" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-14">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Who We Are</div>
              <h2 className="evg-display-lg">
                A governance-led platform sponsor.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-2" data-reveal data-reveal-delay="200">
              <p className="evg-body">
                Evergreen Global Holdings Ltd develops, governs and sponsors strategic investment
                platforms that address critical economic, social and infrastructure needs while
                delivering attractive long-term investor returns.
              </p>
              <div className="mt-14">
                {whoWeAreItems.map((item, i) => (
                  <div
                    key={item}
                    className="evg-editorial-row grid grid-cols-12 gap-6 items-baseline"
                  >
                    <span className="col-span-2 evg-index">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="col-span-10 text-base md:text-lg font-light text-[#0A1F44]">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STRATEGIC FOCUS — editorial stack, no boxed cards */}
      <section data-testid="home-focus" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Three Pillars</div>
            <h2 className="evg-display-lg">Strategic focus areas.</h2>
            <p className="evg-lead mt-8 max-w-2xl">
              Capital deployed across three interconnected pillars that strengthen national
              economies and deliver durable, long-horizon returns.
            </p>
          </div>

          <div>
            {focusAreas.map((f, i) => (
              <div
                key={f.no}
                data-testid={`focus-area-${f.no}`}
                data-reveal
                data-reveal-delay={String(i * 100)}
                className="py-14 md:py-20 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-8"
              >
                <div className="lg:col-span-2 evg-index pt-2">{f.no}</div>
                <h3 className="lg:col-span-6 evg-display-md">{f.title}</h3>
                <div className="lg:col-span-4">
                  <p className="evg-body text-[15px]">{f.body}</p>
                  <div className="mt-7 flex flex-wrap gap-x-4 gap-y-2">
                    {f.pillars.map((p) => (
                      <span key={p} className="evg-meta">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRENT STRATEGIC PLATFORMS — alternating editorial spreads */}
      <section data-testid="home-platforms" className="evg-section bg-white">
        <div className="evg-container">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-24" data-reveal>
            <div className="max-w-2xl">
              <div className="evg-overline mb-8">Current Strategic Platforms</div>
              <h2 className="evg-display-lg">
                Two flagship platforms. One disciplined sponsor.
              </h2>
            </div>
            <Link
              to="/projects"
              data-testid="platforms-view-all"
              className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors self-start"
            >
              View All Projects →
            </Link>
          </div>

          <div className="space-y-28 md:space-y-40">
            {platforms.map((p, idx) => (
              <article
                key={p.title}
                data-testid={p.testId}
                data-reveal
                className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10 items-center"
              >
                <Link
                  to={p.to}
                  className={`lg:col-span-7 evg-img-zoom block ${idx % 2 ? "lg:order-2" : ""}`}
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-[#0A1F44]">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </Link>

                <div className={`lg:col-span-5 ${idx % 2 ? "lg:order-1" : ""}`}>
                  <div className="flex items-baseline gap-x-6 mb-6">
                    <span className="evg-overline-gold">{p.label}</span>
                    <span className="evg-meta">{p.location}</span>
                  </div>
                  <h3 className="evg-display-md">{p.title}</h3>
                  <p className="evg-body mt-7">{p.summary}</p>
                  <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                    {p.tags.map((t) => (
                      <span key={t} className="evg-meta">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-12 pt-10 border-t border-[#0A1F44]/12">
                    <div className="evg-overline">{p.metricLabel}</div>
                    <div className="font-serif text-5xl md:text-6xl mt-3 text-[#0A1F44] tracking-tight">
                      {p.metric}
                    </div>
                    <Link
                      to={p.to}
                      data-testid={`${p.testId}-cta`}
                      className="mt-10 inline-block text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors"
                    >
                      View Platform →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STRATEGIC DEVELOPMENT ALIGNMENT — investment thesis bridge */}
      <section data-testid="home-alignment" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-14 pb-24" data-reveal>
            <div className="lg:col-span-5">
              <div className="evg-overline mb-8">Why These Platforms Matter</div>
              <h2 className="evg-display-lg">
                Creating Value Through Strategic Development Alignment
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-7">
              <p className="evg-body">
                Evergreen Global Holdings Ltd focuses on identifying strategic platforms that
                combine commercial opportunity with long-term economic relevance.
              </p>
              <p className="evg-body">
                Our investment approach is informed by market research, sector analysis and
                validation work undertaken across agriculture, healthcare, tourism and
                infrastructure.
              </p>
              <p className="evg-body">
                The objective is not simply to develop projects, but to create commercially
                sustainable platforms operating in sectors that are widely recognised as important
                to the future development of Saint Vincent &amp; the Grenadines.
              </p>
            </div>
          </div>

          {/* Six themed editorial rows */}
          <div>
            {[
              {
                t: "Food Security & Import Substitution",
                b: "Saint Vincent & the Grenadines continues to rely on imported food products across a number of categories, creating opportunities for domestic production, value-added processing and local supply-chain development. The National Agro-Industrial Platform has been designed to support local agricultural production, processing capacity and long-term food system resilience.",
              },
              {
                t: "Agricultural Value Creation",
                b: "Research undertaken for the National Agro-Industrial Platform identified opportunities to increase value retention through local processing rather than reliance on imported finished products. The platform seeks to create value through cultivation, processing, packaging and distribution within Saint Vincent & the Grenadines.",
              },
              {
                t: "Healthcare Infrastructure",
                b: "Research undertaken for the Bambereaux Integrated Development Platform identified healthcare infrastructure, community care and long-term care capacity as important areas requiring further development and investment. The healthcare component of Bambereaux is intended to contribute to healthcare capacity, patient outcomes and community wellbeing through a clinically governed model.",
              },
              {
                t: "Tourism Development",
                b: "The completion of Argyle International Airport and ongoing investment in tourism infrastructure continue to strengthen Saint Vincent & the Grenadines’ long-term tourism proposition. The hospitality component of Bambereaux is intended to support destination development, visitor growth and broader economic activity linked to the tourism sector.",
              },
              {
                t: "Employment & Skills Development",
                b: "Both platforms have the potential to support employment creation across agriculture, healthcare, hospitality, operations and supporting services. The long-term objective is to create sustainable economic activity that extends beyond the initial development phase.",
              },
              {
                t: "Economic Resilience & Foreign Exchange Retention",
                b: "Local production, value-added processing and export capability can contribute to economic resilience and increased retention of economic value within Saint Vincent & the Grenadines. These themes form an important part of the investment rationale underpinning Evergreen’s current platforms.",
              },
            ].map((row, i) => (
              <div
                key={row.t}
                data-testid={`alignment-theme-${i + 1}`}
                data-reveal
                data-reveal-delay={String((i % 3) * 100)}
                className="py-10 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
              >
                <span className="col-span-12 md:col-span-1 evg-index">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl leading-tight">
                  {row.t}
                </h3>
                <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-[1.8] font-light">
                  {row.b}
                </p>
              </div>
            ))}
          </div>

          {/* Institutional Context */}
          <div
            data-testid="alignment-institutional-context"
            data-reveal
            className="mt-32 grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-10"
          >
            <div className="lg:col-span-5">
              <div className="evg-overline mb-8">Institutional Context</div>
              <h3 className="evg-display-md">
                Commercial sponsor. Aligned with broader economic needs.
              </h3>
            </div>
            <div className="lg:col-span-7 space-y-7">
              <p className="evg-body">Evergreen is not a policy organisation.</p>
              <p className="evg-body">The company is a commercial platform sponsor.</p>
              <p className="evg-body">
                However, our investment philosophy recognises that the strongest long-term
                opportunities often emerge where commercial objectives and broader economic needs
                are naturally aligned.
              </p>
              <p className="evg-body">
                This alignment is considered an important contributor to long-term sustainability,
                stakeholder engagement and value creation.
              </p>
            </div>
          </div>

          {/* Research & Reference Sources */}
          <div
            data-testid="alignment-references"
            data-reveal
            className="mt-28 pt-12 border-t border-[#0A1F44]/12"
          >
            <div className="evg-overline mb-8">Research &amp; Reference Sources</div>
            <p className="max-w-4xl text-[14px] text-[#0A1F44]/65 leading-[1.9] font-light">
              The strategic themes referenced above are informed by research, validation work and
              publicly available materials relating to economic development, agriculture,
              healthcare, tourism and investment within Saint Vincent &amp; the Grenadines,
              including information published by:
            </p>
            <ul className="mt-8 flex flex-wrap gap-x-10 gap-y-3">
              {[
                "Government of Saint Vincent & the Grenadines",
                "Invest SVG",
                "Caribbean Development Bank",
                "World Bank",
                "Caribbean Tourism Organisation",
              ].map((src) => (
                <li
                  key={src}
                  className="evg-meta"
                >
                  {src}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY EVERGREEN — editorial rows on navy */}
      <section data-testid="home-why" className="evg-section bg-[#0A1F44] text-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-16">
            <div className="lg:col-span-4" data-reveal>
              <div className="evg-overline-gold mb-8">Why Evergreen</div>
              <h2 className="evg-display-lg text-white">Six institutional pillars.</h2>
              <p className="mt-8 text-white/70 leading-[1.8] font-light max-w-md">
                The principles by which we evaluate opportunities, structure capital and steward
                long-term value for investors and host communities.
              </p>
            </div>

            <div className="lg:col-span-8">
              {pillars.map((p, i) => (
                <div
                  key={p.title}
                  data-testid={`why-pillar-${i + 1}`}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-10 border-t border-white/15 last:border-b last:border-white/15 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <span className="col-span-12 md:col-span-2 evg-index evg-index-light">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl text-white">
                    {p.title}
                  </h3>
                  <p className="col-span-12 md:col-span-6 text-[15px] text-white/72 leading-relaxed font-light">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
